import React, {ComponentPropsWithoutRef, HTMLAttributes, useState} from 'react';
import styled, {CSSProperties} from 'styled-components';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {ClearIcon, VisibilityOffIconComponent, VisibilityOnIconComponent} from '../Icons';
import {Typography, typographyStyle} from '../Typography';
import ToolTipIcon from './assets/tooltip-icon.png';

interface IForm extends ComponentPropsWithoutRef<'input'> {
  fullWidth?: boolean;
  label?: string;
  error?: boolean | string;
  success?: string;
  info?: string;
  suffix?: string;
  numeric?: boolean;
  icon?: string;
  labelOptions?: {
    position?: 'overlay-top' | 'default';
    width?: number;
  };
  type?: string;
  fontSize?: CSSProperties['fontSize'];
  variant?: 'xlarge' | 'large' | 'medium' | 'small';
  height?: number;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  toolTipMessage?: string;
  readOnlyWithAction?: boolean;
  hasMaskButton?: boolean; // 인풋타입 password인 경우 마스킹 온오프 버튼 유무
  onClear?(): void;
}

export const TextField = React.forwardRef<HTMLInputElement, IForm>(
  (
    {
      variant = 'large',
      height,
      fontSize,
      label,
      labelOptions,
      error,
      info,
      success,
      fullWidth,
      value = '',
      suffix,
      required,
      containerProps,
      numeric,
      icon,
      toolTipMessage,
      readOnly,
      readOnlyWithAction,
      hasMaskButton,
      type,
      onBlur,
      onFocus,
      onChange,
      onClear,
      ...props
    },
    ref
  ) => {
    const isPassword = type === 'password';
    const [hasMask, setHasMask] = useState<boolean>(
      !!hasMaskButton && isPassword
    );

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (props.disabled || readOnly || readOnlyWithAction) {
        return;
      }

      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (props.disabled || readOnly || readOnlyWithAction) {
        return;
      }

      onBlur?.(e);
    };

    const handleClear = (e: any) => {
      e.stopPropagation();
      onClear?.();
    };

    const hasValue = !!value;
    const hasClearButton =
      !!onClear &&
      hasValue &&
      !props.disabled &&
      !readOnly &&
      !readOnlyWithAction;

    const hasLabel = !!label?.length;
    const isFloatingLabel =
      hasLabel && labelOptions?.position === 'overlay-top';

    const hasTooltip = !!toolTipMessage?.length;
    const hasIcon = !!icon?.length;

    return (
      <Root
        hasLabel={hasLabel}
        labelPosition={labelOptions?.position}
        fullWidth={fullWidth}
        {...containerProps}
      >
        <InputWrapper>
          {hasLabel && !isFloatingLabel && (
            <Label maxWidth={labelOptions?.width ?? 114}>
              {label}
              {required && <Typography color={palette.red100}>*</Typography>}
              {hasTooltip && <Tooltip message={toolTipMessage} />}
            </Label>
          )}
          <InputContainer
            error={typeof error === 'string' ? !!error?.length : error}
            variant={variant}
            height={height}
            disabled={props.disabled}
            readOnly={readOnly}
            readOnlyWithAction={readOnlyWithAction}
          >
            {hasIcon && <Icon src={icon} />}
            <InputForm
              {...props}
              type={isPassword && !hasMask ? 'text' : type}
              hasIcon={hasIcon}
              readOnly={readOnly || readOnlyWithAction}
              required={required}
              value={value}
              fontSize={fontSize}
              ref={ref}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <SuffixWrapper>
              {hasMaskButton && isPassword && hasValue ? (
                <MaskButton
                  as={
                    hasMask
                      ? VisibilityOnIconComponent
                      : VisibilityOffIconComponent
                  }
                  color={palette.gray600}
                  onClick={() => setHasMask((prev) => !prev)}
                />
              ) : (
                hasClearButton && (
                  <IconButton
                    src={ClearIcon}
                    alt="clear"
                    onClick={handleClear}
                  />
                )
              )}
              {suffix?.length && (
                <FieldSuffix
                  color={props.disabled ? palette.disabled : palette.gray600}
                  buttonSize={variant}
                >
                  {suffix}
                </FieldSuffix>
              )}
            </SuffixWrapper>
          </InputContainer>
          {isFloatingLabel && (
            <Label float maxWidth={labelOptions?.width ?? 114}>
              {label}
              {required && <Typography color={palette.red100}>*</Typography>}
              {hasTooltip && <Tooltip message={toolTipMessage} />}
            </Label>
          )}
        </InputWrapper>
        {typeof error === 'string' && !!error?.length && (
          <InfoText color={palette.red100}>{error}</InfoText>
        )}
        {!!success?.length && (
          <InfoText color={palette.blue100}>{success}</InfoText>
        )}
        {!!info?.length && (
          <InfoText color={palette.textPrimary}>{info}</InfoText>
        )}
      </Root>
    );
  }
);

const Tooltip = ({message}: {message: string}) => {
  return (
    <ToolTipInfo>
      <ToolTipImg src={ToolTipIcon} />
      <ToolTipMessage>{message}</ToolTipMessage>
    </ToolTipInfo>
  );
};

const Label = styled(Typography)
  .withConfig({
    shouldForwardProp: interceptProps(['float', 'maxWidth']),
  })
  .attrs({
    as: 'label',
    variant: 'medium12',
  })<{
  float?: boolean;
  maxWidth?: number;
}>`
  position: relative;
  display: flex;
  align-items: center;
  color: ${palette.gray600};
  justify-content: flex-start;
  ${({float, maxWidth}) =>
    float
      ? `
    position: absolute;
    top: -8px;
    left: 7px;
    padding: 0 1px;
    background-color: ${palette.white};
  `
      : `
      ${maxWidth && `width: ${maxWidth}px;`}`}
`;

const InputContainer = styled('div').withConfig({
  shouldForwardProp: interceptProps([
    'error',
    'variant',
    'height',
    'readOnly',
    'readOnlyWithAction',
    'disabled',
  ]),
})<{
  error?: boolean;
  readOnly?: boolean;
  readOnlyWithAction?: boolean;
  disabled?: boolean;
  variant?: IForm['variant'];
  height?: number;
}>`
  position: relative;
  display: flex;
  background-color: ${palette.white};
  border-radius: 8px;
  border: solid 1px ${palette.gray200};
  color: ${palette.textPrimary};
  ${({error}) => error && `border-color: ${palette.red100};`}
  ${({disabled, readOnly, readOnlyWithAction}) =>
    !readOnlyWithAction &&
    (disabled || readOnly) &&
    `
    background-color: #eee;
    border-color: #eee !important;
    color:  ${palette.gray300};
    `}
  ${({variant, height}) =>
    `height: ${
      height ||
      (variant === 'medium'
        ? 32
        : variant === 'small'
        ? 24
        : variant === 'xlarge'
        ? 48
        : 42)
    }px;`}
`;

const InputForm = styled('input').withConfig({
  shouldForwardProp: interceptProps(['variant', 'hasIcon', 'fontSize']),
})<{
  variant?: IForm['variant'];
  hasIcon?: boolean;
  fontSize?: CSSProperties['fontSize'];
  type?: string;
}>`
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  ${({variant, fontSize}) =>
    fontSize ||
    (variant === 'small'
      ? {...typographyStyle.medium12}
      : {...typographyStyle.medium14})}
  height: 100%;
  flex-grow: 1;
  border: none;
  padding-left: ${({hasIcon}) => (hasIcon ? 40 : 15)}px;
  background-color: transparent;
  &&& {
    &:disabled {
      cursor: default;
      color: ${palette.disabled};
      &::placeholder {
        color: ${palette.disabled};
      }
    }
  }
  &:enabled:not([readonly]):focus {
    outline: 0;
  }
  &::placeholder {
    color: ${palette.placeholder};
    transition: all 400ms;
  }
  -webkit-autofill {
    color: #fff !important;
  }
  -webkit-appearance: none;
`;

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['fullWidth', 'hasLabel', 'labelPosition']),
})<{
  fullWidth?: boolean;
  hasLabel?: boolean;
  labelPosition?: 'overlay-top' | 'default';
}>`
  width: 100%;
  ${({fullWidth}) => !fullWidth && `max-width: 436px;`}
  ${({hasLabel, labelPosition}) =>
    hasLabel &&
    `    
    ${
      labelPosition === 'overlay-top'
        ? 'padding-top: 8px;'
        : `${InputWrapper} {
      display: flex;
      align-items: center;
    }`
    }
  `}
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Text = styled(Typography)
  .withConfig({
    shouldForwardProp: interceptProps(['buttonSize']),
  })
  .attrs(
    ({
      buttonSize,
      variant,
    }: {
      variant?: string;
      buttonSize?: IForm['variant'];
    }) => ({
      variant: variant ?? (buttonSize === 'small' ? 'medium12' : 'medium14'),
      as: 'div',
    })
  )``;

const Icon = styled.img`
  position: absolute;
  left: 16px;
  top: 50%;
  margin-top: -8px;
  width: 16px;
  height: 16px;
`;

const IconButton = styled.img`
  display: block;
  width: 14px;
  height: 14px;
  cursor: pointer;
  margin-left: 8px;
`;

const MaskButton = styled.div`
  cursor: pointer;
  width: 16px;
  height: 18px;
  margin-left: 8px;
`;

const FieldSuffix = styled(Text)`
  color: ${palette.gray600};
  pointer-events: none;
  margin-left: 8px;
`;

const SuffixWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 16px;
  height: 100%;
  flex-shrink: 0;
`;

const InfoText = styled(Text)`
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  margin-top: 8px;
`;

const ToolTipImg = styled.img`
  width: 100%;
  height: 100%;
`;

const ToolTipMessage = styled.span`
  display: none;
  width: 160px;
  height: 35px;
  padding: 0 5px;
  line-height: 12px;
  opacity: 0.8;
  border-radius: 4px;
  background-color: #2c2c2c;
  font-size: 10px;
  letter-spacing: -0.37px;
  color: white;
`;

const ToolTipInfo = styled.span`
  position: relative;
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-left: 3px;
  cursor: pointer;

  &:hover {
    ${ToolTipMessage} {
      position: absolute;
      left: -25px;
      top: -40px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
  }
`;
